import { scenariosFixture } from "@/features/operations/mock-data";
import type { Scenario } from "@/features/operations/types";

/**
 * シナリオの中身＝トリガー × 条件 × アクション（設計案の核心）。
 * 24シナリオはすべてこの同じ部品の組み合わせなので、ビルダーは 1 つで足りる。
 */
export type StepKind = "trigger" | "condition" | "wait" | "action" | "branch";

export const STEP_KIND_LABEL: Record<StepKind, string> = {
  trigger: "トリガー",
  condition: "条件",
  wait: "待機",
  action: "アクション",
  branch: "分岐"
};

export interface ScenarioStep {
  id: string;
  kind: StepKind;
  title: string;
  detail: string;
  /** ファネル: このステップに到達した人数 */
  entered: number;
  /** ファネル: 次に進んだ人数（トリガーは entered と同じ） */
  advanced: number;
}

export interface DeliverySuggestion {
  metric: string;
  rationale: string;
  before: string;
  after: string;
  /** 期待改善（%） */
  expectedLift: number;
}

export interface ImprovementHunk {
  stepTitle: string;
  before: string;
  after: string;
}

export interface ImprovementProposal {
  summary: string;
  hunks: ImprovementHunk[];
}

export interface ScenarioDefinition {
  id: string;
  segmentId: string | null;
  steps: ScenarioStep[];
  deliverySuggestion: DeliverySuggestion | null;
  improvement: ImprovementProposal | null;
}

const DEFINITIONS: Record<string, Omit<ScenarioDefinition, "id">> = {
  scn_abandoned_checkout: {
    segmentId: "seg_cart_abandoners",
    steps: [
      {
        id: "s1",
        kind: "trigger",
        title: "チェックアウト離脱",
        detail: "checkouts/update Webhook を受信",
        entered: 1420,
        advanced: 1420
      },
      {
        id: "s2",
        kind: "condition",
        title: "RFMスコアで見込み度を判定",
        detail: "RFM ≥ 中位 のみ継続（低スコアは配信抑止）",
        entered: 1420,
        advanced: 1180
      },
      {
        id: "s3",
        kind: "wait",
        title: "1時間待機",
        detail: "離脱直後は避け、1時間後に配信",
        entered: 1180,
        advanced: 1180
      },
      {
        id: "s4",
        kind: "action",
        title: "リマインド配信（カート復元リンク）",
        detail: "件名「カートの商品が残っています」",
        entered: 1180,
        advanced: 612
      },
      {
        id: "s5",
        kind: "condition",
        title: "24時間で未購入か",
        detail: "購入済みはここで完了",
        entered: 612,
        advanced: 418
      },
      {
        id: "s6",
        kind: "action",
        title: "クーポン投入（最終）",
        detail: "10%オフクーポンで最後のひと押し",
        entered: 418,
        advanced: 214
      },
      {
        id: "s7",
        kind: "branch",
        title: "未反応 → 休眠掘り起こしフローへ",
        detail: "別シナリオで追客を継続",
        entered: 204,
        advanced: 204
      }
    ],
    deliverySuggestion: {
      metric: "開封率",
      rationale: "過去14回の配信で、件名に商品名を含めた回では開封率が平均 6.2pt 高い。",
      before: "カートの商品が残っています",
      after: "「{商品名}」がカートに残っています",
      expectedLift: 6.2
    },
    improvement: {
      summary:
        "1時間待機は離脱直後の熱量を逃している。ファネルでは s4 の配信で 48% が離脱。待機を 20 分に短縮する案。",
      hunks: [
        {
          stepTitle: "1時間待機",
          before: "離脱から 60 分後に配信",
          after: "離脱から 20 分後に配信"
        }
      ]
    }
  },
  scn_welcome_series: {
    segmentId: "seg_first_time",
    steps: [
      {
        id: "s1",
        kind: "trigger",
        title: "会員・購読登録",
        detail: "customers/create Webhook を受信",
        entered: 1284,
        advanced: 1284
      },
      {
        id: "s2",
        kind: "action",
        title: "ウェルカム配信（即時）",
        detail: "ブランド紹介を登録直後に送信",
        entered: 1284,
        advanced: 902
      },
      {
        id: "s3",
        kind: "wait",
        title: "3日待機",
        detail: "初回購入を促す前の間",
        entered: 902,
        advanced: 902
      },
      {
        id: "s4",
        kind: "condition",
        title: "未購入か",
        detail: "購入済みは購入後フォローへ",
        entered: 902,
        advanced: 638
      },
      {
        id: "s5",
        kind: "action",
        title: "初回クーポン配布",
        detail: "初回限定 15%オフ",
        entered: 638,
        advanced: 312
      },
      {
        id: "s6",
        kind: "branch",
        title: "初回購入 → 購入後フォローへ",
        detail: "育成フローに自動バトンタッチ",
        entered: 312,
        advanced: 312
      }
    ],
    deliverySuggestion: {
      metric: "転換率",
      rationale: "クーポン有効期限を明示した配信は、していない配信より転換率が 2.1pt 高い。",
      before: "初回限定クーポンをお使いください",
      after: "初回限定クーポン（7日間有効）をお使いください",
      expectedLift: 2.1
    },
    improvement: null
  },
  scn_vip: {
    segmentId: "seg_vip",
    steps: [
      {
        id: "s1",
        kind: "trigger",
        title: "RFM上位20%入り",
        detail: "スコア再計算で VIP セグメントに追加",
        entered: 486,
        advanced: 486
      },
      {
        id: "s2",
        kind: "action",
        title: "VIPタグ付与",
        detail: "セグメントを自動更新し配信対象を切替",
        entered: 486,
        advanced: 486
      },
      {
        id: "s3",
        kind: "action",
        title: "限定オファー配信",
        detail: "先行販売・限定特典を VIP だけに案内",
        entered: 486,
        advanced: 312
      },
      {
        id: "s4",
        kind: "condition",
        title: "購買ペース低下を検知",
        detail: "低下した VIP は離脱防止フローへ",
        entered: 486,
        advanced: 44
      },
      {
        id: "s5",
        kind: "branch",
        title: "ペース低下 → 離脱防止フローへ",
        detail: "休眠化する前に先回りで対応",
        entered: 44,
        advanced: 44
      }
    ],
    deliverySuggestion: null,
    improvement: null
  }
};

/** 定義が無いシナリオでも、fixture から最小限のトリガー→アクション構成を生成する。 */
function fallbackDefinition(scenario: Scenario): Omit<ScenarioDefinition, "id"> {
  const base = Math.max(scenario.deliveries24h, 20);
  return {
    segmentId: null,
    steps: [
      {
        id: "s1",
        kind: "trigger",
        title: scenario.trigger,
        detail: `${scenario.trigger} を受信するとフローを起動`,
        entered: base,
        advanced: base
      },
      {
        id: "s2",
        kind: "condition",
        title: "配信対象を判定",
        detail: "セグメント条件に一致する顧客に絞る",
        entered: base,
        advanced: Math.round(base * 0.82)
      },
      {
        id: "s3",
        kind: "action",
        title: "配信",
        detail: "テンプレートに沿ってメールを送信",
        entered: Math.round(base * 0.82),
        advanced: Math.round(base * 0.82 * (scenario.openRate / 100 || 0.4))
      },
      {
        id: "s4",
        kind: "action",
        title: "成果を記録",
        detail: "開封・クリック・転換をダッシュボードに反映",
        entered: Math.round(base * 0.82 * (scenario.openRate / 100 || 0.4)),
        advanced: scenario.conversions24h
      }
    ],
    deliverySuggestion: null,
    improvement: null
  };
}

export function getScenarioDefinition(id: string): ScenarioDefinition | null {
  const scenario = scenariosFixture.find((entry) => entry.id === id);
  if (!scenario) return null;
  const defined = DEFINITIONS[id];
  return { id, ...(defined ?? fallbackDefinition(scenario)) };
}

export function getScenario(id: string): Scenario | undefined {
  return scenariosFixture.find((entry) => entry.id === id);
}

export type ProposalKind = "delivery" | "improvement";

export interface PendingProposal {
  scenarioId: string;
  scenarioName: string;
  kind: ProposalKind;
  summary: string;
}

/**
 * 承認待ちのAI提案を横断で集約する。AIOps（第4の軸）は独立画面ではなく、
 * 概況の入口 + 各シナリオの承認 UI として現れる（画面一覧の集約方針）。
 */
export function getPendingProposals(): PendingProposal[] {
  const proposals: PendingProposal[] = [];
  for (const scenario of scenariosFixture) {
    const definition = DEFINITIONS[scenario.id];
    if (!definition) continue;
    if (definition.deliverySuggestion) {
      proposals.push({
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        kind: "delivery",
        summary: `${definition.deliverySuggestion.metric}を +${definition.deliverySuggestion.expectedLift}% 見込む配信改善案`
      });
    }
    if (definition.improvement) {
      proposals.push({
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        kind: "improvement",
        summary: definition.improvement.summary
      });
    }
  }
  return proposals;
}
